import { FC, useId } from "react";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import {
    Picker,
    type PickerItemData,
    type PickerSectionData,
} from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import {
    ExternalSystemIcon,
    PICKABLE_EXTERNAL_SYSTEM_OPTIONS,
    getExternalSystemLabel,
    toExternalSystemChoice,
    type ExternalSystemChoice,
} from "@components/accountingMigration/source";
import styles from "./HistoricalDataFields.module.scss";

const isConnectChoice = (choice: ExternalSystemChoice) =>
    choice === AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE;

const SYSTEM_ITEMS: Array<PickerItemData<ExternalSystemChoice>> =
    PICKABLE_EXTERNAL_SYSTEM_OPTIONS.map(({ system, institution }) => ({
        value: toExternalSystemChoice(system),
        label: getExternalSystemLabel(system),
        leading: <ExternalSystemIcon institution={institution} />,
    }));

const SYSTEM_SECTIONS: Array<PickerSectionData<ExternalSystemChoice>> = [
    {
        key: "connect",
        title: "Connect",
        items: SYSTEM_ITEMS.filter((item) => isConnectChoice(item.value)),
    },
    {
        key: "upload",
        title: "Upload general ledger",
        items: SYSTEM_ITEMS.filter((item) => !isConnectChoice(item.value)),
    },
];

interface Props {
    value: ExternalSystemChoice | null;
    onChange: (value: ExternalSystemChoice) => void;
    disabled?: boolean;
}

export const MigrateFromSelector: FC<Props> = ({
    value,
    onChange,
    disabled,
}) => {
    const labelId = useId();
    const selectedItem = SYSTEM_ITEMS.find((item) => item.value === value);

    return (
        <VStack gap={8} paddingBlock={16} paddingInline={24}>
            <div id={labelId} className={styles.migrationSectionLabel}>
                Migrate from
            </div>
            <Picker.Select
                sections={SYSTEM_SECTIONS}
                value={value}
                onValueChange={(choice) => {
                    if (choice !== null) {
                        onChange(choice);
                    }
                }}
                disabled={disabled}
                placeholder="Select software"
                trigger={
                    <Picker.Button
                        aria-labelledby={labelId}
                        variant="primary"
                        size="xl"
                        block
                        leftIcon={selectedItem?.leading}
                    />
                }
            />
        </VStack>
    );
};
